export type UxPageType =
  | 'collection'
  | 'entity-detail'
  | 'child-workflow'
  | 'tabbed-entity'
  | 'operational-workspace'
  | 'create-edit'
  | 'focused-dialog'
  | 'global-panel';

export type BreadcrumbItem = {
  label: string;
  path?: string;
  current?: boolean;
};

export type CanonicalRouteContext = {
  page: string;
  label: string;
  path?: string;
  pageType: UxPageType;
  parent?: CanonicalRouteContext;
};

export type ReturnOrigin = {
  label: string;
  page: string;
  path?: string;
  context?: Record<string, string | null | undefined>;
};

export type ContextBackDestination = {
  label: string;
  page: string;
  path?: string;
  source: 'return-origin' | 'canonical-parent' | 'fallback';
  context?: Record<string, string | null | undefined>;
};

export function buildBreadcrumbTrail(context: CanonicalRouteContext): BreadcrumbItem[] {
  const trail: BreadcrumbItem[] = [];
  let cursor: CanonicalRouteContext | undefined = context;

  while (cursor) {
    trail.unshift({
      label: cursor.label,
      path: cursor.path,
      current: cursor === context,
    });
    cursor = cursor.parent;
  }

  return trail.map((item, index) => ({
    ...item,
    current: index === trail.length - 1,
    path: index === trail.length - 1 ? undefined : item.path,
  }));
}

export function resolveContextBackDestination(
  current: CanonicalRouteContext,
  returnOrigin: ReturnOrigin | null | undefined,
  fallback: ReturnOrigin,
): ContextBackDestination {
  if (returnOrigin) {
    return {
      label: returnOrigin.label,
      page: returnOrigin.page,
      path: returnOrigin.path,
      context: returnOrigin.context,
      source: 'return-origin',
    };
  }

  if (current.parent) {
    return {
      label: current.parent.label,
      page: current.parent.page,
      path: current.parent.path,
      source: 'canonical-parent',
    };
  }

  return {
    label: fallback.label,
    page: fallback.page,
    path: fallback.path,
    context: fallback.context,
    source: 'fallback',
  };
}

export function matchCanonicalContext(matchLabel: string, matchPath?: string, origin: 'team-events' | 'planning-board' = 'team-events'): CanonicalRouteContext {
  return {
    page: 'match-detail',
    label: matchLabel,
    path: matchPath,
    pageType: 'entity-detail',
    parent: origin === 'planning-board'
      ? {
        page: 'planning-board',
        label: 'Planning Board',
        path: '/planning-board',
        pageType: 'operational-workspace',
      }
      : {
        page: 'team-events',
        label: 'Team Events',
        path: '/team-events',
        pageType: 'collection',
      },
  };
}

export function availabilityCanonicalContext(
  matchLabel: string,
  origin: 'planning-board' | 'match-detail' | 'team-events' | 'direct' = 'direct',
  matchPath?: string,
): CanonicalRouteContext {
  const matchContext: CanonicalRouteContext = {
    page: 'availability-match',
    label: matchLabel,
    path: origin === 'match-detail' ? matchPath : undefined,
    pageType: 'entity-detail',
    parent: origin === 'match-detail' || origin === 'team-events'
      ? {
        page: 'team-events',
        label: 'Team Events',
        path: '/team-events',
        pageType: 'collection',
      }
      : {
        page: 'planning-board',
        label: 'Planning Board',
        path: '/planning-board',
        pageType: 'operational-workspace',
      },
  };

  return {
    page: 'availability',
    label: 'Player Availability',
    pageType: 'operational-workspace',
    parent: matchContext,
  };
}

export function lineupCanonicalContext(
  matchLabel: string,
  matchPath: string | undefined,
  lineupLabel = 'Lineup Builder',
  origin: 'team-events' | 'planning-board' = 'team-events',
): CanonicalRouteContext {
  return {
    page: 'lineup',
    label: lineupLabel,
    pageType: 'child-workflow',
    parent: matchCanonicalContext(matchLabel, matchPath, origin),
  };
}

export function teamEventCanonicalContext(eventLabel: string, eventPath?: string): CanonicalRouteContext {
  return {
    page: 'team-event-detail',
    label: eventLabel,
    path: eventPath,
    pageType: 'entity-detail',
    parent: {
      page: 'team-events',
      label: 'Team Events',
      path: '/team-events',
      pageType: 'collection',
    },
  };
}

export function practiceRotationCanonicalContext(eventLabel: string, eventPath?: string): CanonicalRouteContext {
  return {
    page: 'practice-rotation',
    label: 'Practice Rotation',
    pageType: 'child-workflow',
    parent: teamEventCanonicalContext(eventLabel, eventPath),
  };
}
